import { Injectable, Injector } from '@angular/core';
import { SdkBase } from '../utilities/sdk-base/sdk.base';
import { map, Observable } from 'rxjs';
import { ODataRequest } from '../utilities/sdk-base/o-data-request';
import { ReviewSetResource } from '../models/resource/review-set.resource';
import { ReviewSetResourceToReviewSetMapper } from '../models/mappers/review-set-resource-to-review-set.mapper';
import { ReviewSet } from '../models/ui/review-set.model';
import { Guid } from '../models/guid';
import { ReviewSetToReviewSetResourceMapper } from '../models/mappers/review-set-to-review-set-resource.mapper';
import { ReportRequestParams } from '../models/resource/report-request-params.resource';

@Injectable({ providedIn: 'root' })
export class ReviewSetSdk extends SdkBase {
	constructor(injector: Injector) {
		super(injector);
	}

	public get(): Observable<ReviewSet[]> {
		const request = new ODataRequest<ReviewSetResource>('get', 'ReviewSets');
		return this.makeODataRequest$<ReviewSetResource[]>(request, 'api2').pipe(
			map((response) => {
				const mapper = new ReviewSetResourceToReviewSetMapper();
				return mapper.mapCollection(response);
			})
		);
	}
	
	public nextDocument(params: NextDocumentParams): Observable<string> {
		return this.makeHttpRequest<any>('get', `api2/reviewSets/${ params?.reviewSetId }/docs/${ params?.currentDocumentId || Guid.Empty }/next`);
	}

	public checkInReviewSet(params: CheckInReviewSetParams): Observable<string> {
		return this.makeHttpRequest('get', `api2/reviewSets/${ params?.reviewSetId }/checkIn`);
	}

	public startReviewSet(params: CheckInReviewSetParams): Observable<string> {
		return this.makeHttpRequest('get', `api2/reviewSets/${ params?.reviewSetId }/start`);
	}

	public stopReviewSet(params: CheckInReviewSetParams): Observable<string> {
		return this.makeHttpRequest('get', `api2/reviewSets/${ params?.reviewSetId }/stop`);
	}

	public post(params: PostReviewSetParams): Observable<ReviewSet> {
		const mapper = new ReviewSetToReviewSetResourceMapper();
		const set = mapper.map(params.reviewSet);
		const request = new ODataRequest('post', 'ReviewSets', set);
		return this.makeODataRequest$<ReviewSetResource>(request, 'api2').pipe(
			map((response) => {
				const reviewSetResourceToReviewSetMapper = new ReviewSetResourceToReviewSetMapper();
				return reviewSetResourceToReviewSetMapper.map(response);
			})
		);
	}

	public put(params: PostReviewSetParams): Observable<void> {
		const mapper = new ReviewSetToReviewSetResourceMapper();
		const set = mapper.map(params.reviewSet);
		const request = new ODataRequest('put', 'ReviewSets', set.reviewSetId, set);
		return this.makeODataRequest$(request, 'api2');
	}

	public delete(params: PostReviewSetParams): Observable<void> {
		const request = new ODataRequest('delete', 'ReviewSets', params.reviewSet.id);
		return this.makeODataRequest$(request, 'api2');
	}

	public generateReportData(params: ReportRequestParams = null): Observable<Record<string, string>[]> {
		return this.makeHttpRequest('post', 'api2/reviewSets/generate', params);
	}

	public splitReviewSet(params: SplitReviewSetParams): Observable<void> {
		return this.makeHttpRequest('post', 'api2/reviewSets/split-review-set', params);
	}

	public confirmSplitJob(params: ConfirmSplitReviewSetParams):Observable<any>{
		return this.makeHttpRequest('post', 'api2/reviewSets/comfirm-split', params);
	}
}

interface NextDocumentParams {
	reviewSetId: string;
	currentDocumentId?: string;
}

interface CheckInReviewSetParams {
	reviewSetId: string;
}

interface PostReviewSetParams {
	reviewSet: ReviewSet;
}

interface SplitReviewSetParams {
	reviewSetId: string;
	numOfBatches: number;
}

interface ConfirmSplitReviewSetParams {
	reviewSetId: string;
	confirm: boolean;
}